import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { RouteComponentProps, useLocation } from 'react-router-dom';
import ReactFocusLock from 'react-focus-lock';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@socialchorus/shared-ui-components';
import { useRecoilState } from 'recoil';

import { Curtain } from '../../components/ui';
import { usePatronSelector } from '../../common/use-patron-selector';
import { uiSelectors } from '../../models/ui';
import { useFeatureFlag } from '../../common/use-feature-flag';
import { Feature } from '../../models/features/features';
import { setPageScroll } from '../../patron-routes';
import { chatbotSessionState } from '../../models/chatbot/recoil-state';
import programSelectors from '../../models/program/selectors';
import advocateSelectors from '../../models/advocate/selectors';
import {
  clearChatThreadId,
  saveChatThreadId,
} from '../../lib/chat-session-storage';
import { ChatHistoryPanel } from './chat-history-panel';
import {
  trackChatbotThreadResumeFromHistory,
  trackChatbotThreadResumeFromSearch,
} from '../../models/assistant/analytics/analytics';
import styles from './assistant-side-panel.module.scss';

const ANIMATION_DURATION = 200;

export type AssistantSidePanelProps = RouteComponentProps & {
  goBack: {
    pathname?: string;
    search?: string;
    hash?: string;
    state?: unknown;
  } | null;
  children?: ReactNode;
};

export const AssistantSidePanel: React.FC<AssistantSidePanelProps> = ({
  history,
  goBack,
  children,
}) => {
  const { t } = useTranslation();
  // Side panel can now access the nested router location via useLocation()
  const location = useLocation();

  const [closing, setClosing] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  // Persistent back context: set when the user resumes a thread from
  // history/search, cleared on "new conversation" or fresh history open. Drives
  // both the header back button (visible whenever non-null) and the initial
  // search query of the panel on back-navigation (always passed as initialQuery
  // — the history icon is suppressed while this is non-null, so fresh opens
  // clear it first).
  const [historyBackContext, setHistoryBackContext] = useState<{
    query: string;
  } | null>(null);
  // Track whether history was opened from the "new chat" (empty) state so the
  // back button can restore it instead of triggering an auto-resume.
  const wasNewConversationRef = useRef(false);
  const chatHistoryEnabled = useFeatureFlag(Feature.CHATBOT_HISTORY_ENABLED);
  const overlayIsActive = usePatronSelector((state) =>
    uiSelectors.getOverlayIsActive(state)
  );

  const [chatbotSession, setChatbotSession] =
    useRecoilState(chatbotSessionState);
  const { threadId } = chatbotSession;

  const programId = usePatronSelector(programSelectors.getProgramId);
  const advocateId = usePatronSelector(advocateSelectors.getAdvocateId);

  // Check if we're on the chatbot home route (base assistant path)
  const isOnChatbotRoute =
    location.pathname === '/' ||
    location.pathname === '' ||
    location.pathname === '/assistant';

  const close = useCallback(() => {
    goBack?.pathname ? history.replace(goBack.pathname) : history.goBack();
  }, [history, goBack?.pathname]);

  const handleCloseClick = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      close();
    }, ANIMATION_DURATION);
  }, [close]);

  const resetToNewChat = useCallback(() => {
    if (advocateId && programId) {
      clearChatThreadId(String(advocateId), String(programId));
    }
    setChatbotSession((prev) => ({
      ...prev,
      threadId: undefined,
      isThreadPersisted: false,
      shouldFocusInput: true,
      skipResume: true,
    }));
  }, [setChatbotSession, advocateId, programId]);

  const handleNewConversation = useCallback(() => {
    resetToNewChat();
    setIsHistoryOpen(false);
    setHistoryBackContext(null);
  }, [resetToNewChat]);

  const handleHistoryOpen = useCallback(() => {
    wasNewConversationRef.current = !threadId;
    setHistoryBackContext(null);
    setIsHistoryOpen(true);
  }, [threadId]);

  const handleHistoryClose = useCallback(() => {
    if (wasNewConversationRef.current) {
      resetToNewChat();
    }
    setIsHistoryOpen(false);
    // Keep historyBackContext so the back button remains available after the
    // panel closes back onto the resumed chat.
  }, [resetToNewChat]);

  const handleBackToHistory = useCallback(() => {
    if (!historyBackContext) return;
    setIsHistoryOpen(true);
  }, [historyBackContext]);

  const handleSelectThread = useCallback(
    (
      selectedThreadId: string,
      source: 'history' | 'search',
      query?: string
    ) => {
      wasNewConversationRef.current = false;
      setChatbotSession((prev) => ({
        ...prev,
        threadId: selectedThreadId,
        isThreadPersisted: true,
      }));
      if (advocateId && programId) {
        saveChatThreadId(
          String(advocateId),
          String(programId),
          selectedThreadId
        );
      }
      setIsHistoryOpen(false);
      setHistoryBackContext({ query: query ?? '' });
      if (source === 'search') {
        trackChatbotThreadResumeFromSearch({ thread_id: selectedThreadId });
      } else {
        trackChatbotThreadResumeFromHistory({ thread_id: selectedThreadId });
      }
    },
    [setChatbotSession, advocateId, programId]
  );

  useEffect(() => {
    if (closing) {
      setPageScroll(true);
    }
  }, [closing]);

  return (
    <>
      <Curtain
        onClick={handleCloseClick}
        className="assistant-side-panel"
        enabled={!closing}
      />
      <ReactFocusLock disabled={!!overlayIsActive}>
        <div
          className={cx(
            styles.assistantSidePanel,
            closing && styles.closing,
            'assistant'
          )}
        >
          <div className={styles.header}>
            <div className={cx('semibold text-tertiary-heading', styles.title)}>
              {isHistoryOpen
                ? t('assistant.history', 'History')
                : t('assistant.assistant')}
            </div>
            <div className={styles.headerActions}>
              {isOnChatbotRoute && (threadId || isHistoryOpen) && (
                <IconButton
                  iconName="edit_square"
                  onClick={handleNewConversation}
                  title="New conversation"
                  aria-label="New conversation"
                />
              )}
              {chatHistoryEnabled &&
                isOnChatbotRoute &&
                !isHistoryOpen &&
                !historyBackContext && (
                  <IconButton
                    iconName="history"
                    onClick={handleHistoryOpen}
                    title={t('assistant.history', 'History')}
                    aria-label={t('assistant.history', 'History')}
                  />
                )}
              {chatHistoryEnabled &&
                isOnChatbotRoute &&
                (isHistoryOpen || historyBackContext) && (
                  <IconButton
                    iconName="arrow_back"
                    onClick={
                      isHistoryOpen ? handleHistoryClose : handleBackToHistory
                    }
                    title={t('common.back', 'Back')}
                    aria-label={t('common.back', 'Back')}
                  />
                )}
              <IconButton
                iconName="close"
                onClick={handleCloseClick}
                title={t('common.close', 'Close')}
                aria-label={t('common.close', 'Close')}
              />
            </div>
          </div>
          <div
            className={cx(styles.content, {
              [styles.chatbotContent]: isOnChatbotRoute,
            })}
          >
            {chatHistoryEnabled && isOnChatbotRoute && isHistoryOpen ? (
              <ChatHistoryPanel
                onSelectThread={handleSelectThread}
                initialQuery={historyBackContext?.query}
              />
            ) : (
              children
            )}
          </div>
        </div>
      </ReactFocusLock>
    </>
  );
};
